import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePromoCodes } from "@/hooks/usePromoCodes";
import { PromoCode, PromoCodeUsage } from "@/types/admin";
import { PromoCodeForm } from "@/components/admin/promo/PromoCodeForm";
import { PromoCodeUsageList } from "@/components/admin/promo/PromoCodeUsageList";
import { ArrowLeft, Tag } from "lucide-react";

export default function AdminPromoCodeDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getPromoCode, updatePromoCode, getPromoCodeUsages, loading } = usePromoCodes();
  
  const [promo, setPromo] = useState<PromoCode | null>(null);
  const [usages, setUsages] = useState<PromoCodeUsage[]>([]);

  const loadData = useCallback(async (promoId: string) => {
    const [p, u] = await Promise.all([
      getPromoCode(promoId),
      getPromoCodeUsages(promoId)
    ]);
    setPromo(p);
    setUsages(u);
  }, [getPromoCode, getPromoCodeUsages]);

  useEffect(() => {
    if (id) {
      loadData(id);
    }
  }, [id, loadData]);

  const handleUpdate = async (data: Partial<PromoCode>) => {
    if (id) {
      const result = await updatePromoCode(id, data);
      if (result) {
        setPromo(result);
      }
    }
  };

  if (loading && !promo) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-black border-t-transparent" />
      </div>
    );
  }

  if (!promo) {
    return <div className="p-8 text-center text-red-500 font-bold">{t('admin.promoCodeDetail.notFound')}</div>;
  }

  return (
    <div className="mx-auto max-w-7xl px-6 md:px-8 py-8">
      {/* Breadcrumbs */}
      <button 
        onClick={() => navigate('/admin/promo-codes')}
        className="mb-6 flex items-center gap-2 font-bold text-gray-500 hover:text-black"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('common.backToList')}
      </button>

      <div className="mb-8 flex items-center gap-4">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl border-2 border-black bg-[#FFD93D] shadow-[4px_4px_0_#000]">
          <Tag className="h-8 w-8" />
        </div>
        <div>
          <h2 className="text-4xl font-black tracking-tighter text-[#1A1A1A]">{promo.code}</h2>
          <p className="font-bold text-gray-500">{t('admin.promoCodeDetail.details')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        {/* Main Settings Form */}
        <div className="lg:col-span-2 space-y-8">
          <div className="rounded-2xl border-2 border-black bg-white p-8 shadow-[4px_4px_0_#000]">
            <h3 className="mb-6 text-xl font-black">{t('admin.promoCodeDetail.basicSettings')}</h3>
            <PromoCodeForm 
              initialData={promo} 
              onSubmit={handleUpdate} 
              loading={loading} 
            />
          </div>
        </div>

        {/* Sidebar: Usage Statistics */}
        <div className="space-y-8">
          <div className="rounded-2xl border-2 border-black bg-[#6FEDD6] p-6 shadow-[4px_4px_0_#000]">
            <h3 className="mb-4 text-sm font-black uppercase tracking-wider text-black/60">{t('admin.promoCodeDetail.usageStats')}</h3>
            <div className="flex items-end gap-2">
              <span className="text-5xl font-black">{promo.used_count}</span>
              <span className="mb-2 font-bold text-black/60">
                / {promo.max_uses ? t('admin.promoCodeDetail.usageCount', { max: promo.max_uses }) : t('admin.promoCodeDetail.unlimited')}
              </span>
            </div>
            {promo.max_uses && (
              <div className="mt-4 h-3 w-full border-2 border-black bg-white rounded-full overflow-hidden">
                <div 
                  className="h-full bg-black/20" 
                  style={{ width: `${(promo.used_count / promo.max_uses) * 100}%` }} 
                />
              </div>
            )}
          </div>

          <PromoCodeUsageList usages={usages} loading={loading} />
        </div>
      </div>
    </div>
  );
}
